import { useRef, useState } from "react";
import "../../styles/AdminDashboard.css";
import SearchIcon from "../icons/SearchIcon";
import useSearchUsers from "../../hooks/useSearchUsers";
import RefreshIcon from "../icons/RefreshIcon";
import CancelIcon from "../icons/CancelIcon";
import { AddAdminIcon, RemoveAdminIcon } from "../icons/AdminIcons";
import useUpdateUserRole from "../../hooks/useUpdateUserRole";
import Toast from "../Toast";
import formatDate from "../../utils/formatDate";

function UsersTab({ error, isLoadingUsers, users, refetch }) {
  const [searchQuery, setSearchQuery] = useState(""); // Input value for searching user

  const searchInputRef = useRef(null); // Ref to the search input for clearing the values when needed

  // Tanstack Query custom hook for searching users by name and caching search results
  const { searchUsers, searchedUsers, isLoadingSearchedUsers, searchError, reset } =
    useSearchUsers();

  // Tanstack Query custom hook for updating user roles
  const { updateRole, isUpdatingRole, errorUpdatingRole } = useUpdateUserRole();

  // Search a user by name
  const handleSearchUser = (query) => {
    searchUsers(query); // This will trigger the useSearchUsers hook to search for users matching the query.
  };

  const handleClearSearch = () => {
    reset(); // Clear search results and errors from the useSearchUsers hook
    setSearchQuery(""); // Clear the search query

    // Clear the search input field
    if (searchInputRef?.current) {
      searchInputRef.current.value = "";
    }
  };

  // Refetch ALL users from Supabase (refresh button action)
  const handleRefreshUsers = () => {
    setSearchQuery("");
    reset(); // Clear search results and errors from the useSearchUsers hook
    refetch(); // This will trigger the useFetchUsers hook to fetch the latest users from Supabase and update the cache.
  };

  // Promote a user to admin
  const handleMakeAdmin = (userId) => {
    updateRole({
      userId,
      newRole: "admin",
    });
  };

  // Demote an admin back to a normal user
  const handleRemoveAdmin = (userId) => {
    updateRole({
      userId,
      newRole: "user",
    });
  };

  // If there are searched users, display them; otherwise, display all users
  const displayedUsers = searchedUsers.length > 0 ? searchedUsers : users;

  return (
    <div className="adminDash__panel">
      {/* Toast notification */}
      {errorUpdatingRole && <Toast message={`⚠️ ${errorUpdatingRole}`} />}

      {/* Error fetching users */}
      {error && (
        <div className="adminDash__error" role="alert">
          {error}
        </div>
      )}

      {/* Search Section */}
      <div className="adminDash__searchSection">
        <div className="adminDash__searchWrap">
          {/* Search Input */}
          <input
            ref={searchInputRef}
            type="text"
            name="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="adminDash__searchInput"
            placeholder="Search users by name"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearchUser(searchQuery);
              }
            }}
          />

          {/* Search Btn*/}
          <button
            onClick={() => handleSearchUser(searchQuery)}
            className="adminDash__searchBtn"
            type="button"
          >
            <SearchIcon />
          </button>
        </div>

        {/* Clear Search Button + Refresh Button*/}
        <div style={{ display: "flex", gap: "0.75rem" }}>
          {/* Clear Search Button */}
          {searchQuery && (
            <button
              className="adminDash__clearBtn"
              onClick={handleClearSearch}
              type="button"
              title="Clear search"
            >
              <CancelIcon />
            </button>
          )}

          {/* Refresh Button */}
          <button
            className="adminDash__refreshBtn"
            onClick={handleRefreshUsers}
            type="button"
            title="Refresh users"
          >
            <RefreshIcon />
          </button>
        </div>
      </div>

      {/* Error Searching for user(s) */}
      {searchError && (
        <div className="adminDash__error" role="alert" style={{ marginBottom: "1rem" }}>
          {searchError}
        </div>
      )}

      {/* Loading users from Supabase */}
      {isLoadingUsers || isLoadingSearchedUsers ? (
        <div className="adminDash__loading">Loading users...</div>
      ) : (
        <>
          <div className="adminDash__tableWrap">
            <table className="adminDash__table">
              {/* Table Header */}
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Occupation</th>
                  <th>State</th>
                  <th>LGA</th>
                  <th>Role</th>
                  <th>Joined</th>
                </tr>
              </thead>

              <tbody>
                {displayedUsers?.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="adminDash__empty">
                      No users registered yet.
                    </td>
                  </tr>
                ) : (
                  // Display users
                  displayedUsers.map((user) => (
                    <tr key={user?.id}>
                      {/* Make / Remove Admin Button */}
                      <td>
                        {user?.role === "admin" ? (
                          <button
                            className="adminDash__removeAdminBtn"
                            type="button"
                            title="Remove admin"
                            disabled={isUpdatingRole}
                            onClick={() => handleRemoveAdmin(user?.id)}
                          >
                            <RemoveAdminIcon />
                          </button>
                        ) : (
                          <button
                            className="adminDash__makeAdminBtn"
                            type="button"
                            title="Make admin"
                            disabled={isUpdatingRole}
                            onClick={() => handleMakeAdmin(user?.id)}
                          >
                            <AddAdminIcon />
                          </button>
                        )}
                      </td>
                      <td className="adminDash__tdName">{user?.full_name || "—"}</td>
                      <td>{user?.email || "—"}</td>
                      <td>{user?.occupation || "—"}</td>
                      <td>{user?.state || "—"}</td>
                      <td>{user?.lga || "—"}</td>
                      <td>
                        <span className={`adminDash__badge adminDash__badge--${user?.role}`}>
                          {user?.role || "user"}
                        </span>
                      </td>
                      <td className="adminDash__tdDate">
                        {user?.joined_at ? formatDate(user?.joined_at) : "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer */}
          <p className="adminDash__tableFooter">
            Showing {displayedUsers?.length} registered user
            {displayedUsers?.length !== 1 ? "s" : ""}
          </p>
        </>
      )}
    </div>
  );
}

export default UsersTab;
